import { useBooleanState } from '@cian/react-utils';

import { useCallback, useMemo } from 'react';

import { type SortType } from '@/packages/api-models/common/json_query';
import { useGetHeaderHeight } from '@/packages/hybrid-search-page-layout/hooks/useGetHeaderHeight';
import { useGetOffersByActivePinQuery } from '@/packages/query-state/shared/store/search-offers/v1/get-offers-by-pin/query';
import { sortOptionsFromGetOffersByPinSelector } from '@/packages/query-state/shared/store/search-offers/v1/get-offers-by-pin/selectors';
import { memoWithDisplayName } from '@/packages/react-utils/memoWithDisplayName';
import { useIntersectionObserver } from '@/packages/react-utils/useIntersectionObserver';
import { boundActionsFactory } from '@/packages/redux-state/store';
import { useListingLayoutFiltersHeightSelector } from '@/packages/redux-state/store/slices/listingLayout';
import { useActivePinSortSelector } from '@/packages/redux-state/store/slices/mapControls';

import { SortListingButton } from './components/SortListingButton';
import { type IOption } from './utils/Option';
import { getSortOptions } from './utils/mappers';

type HandleItemClick = (item: IOption<string, SortType>) => void;

const useBoundActions = boundActionsFactory(reduxActions => ({
  setActivePinSortWithPageReset: reduxActions.mapControls.setActivePinSortWithPageReset,
}));

export const SortListingByPinButtonContainer = memoWithDisplayName(() => {
  const boundActions = useBoundActions();

  const { data: sortOptions, refetch } = useGetOffersByActivePinQuery({
    select: sortOptionsFromGetOffersByPinSelector,
  });
  const filtersHeight = useListingLayoutFiltersHeightSelector();
  const sort = useActivePinSortSelector();
  const headerHeight = useGetHeaderHeight();

  const dropdownVisibilityState = useBooleanState();
  const { setFalse: closeDropdown } = dropdownVisibilityState;

  const { elementRef } = useIntersectionObserver<HTMLButtonElement>(
    {
      observerOptions: { rootMargin: `-${filtersHeight + headerHeight}px 0px 0px 0px` },
      onOutViewport() {
        if (dropdownVisibilityState.state) {
          closeDropdown();
        }
      },
    },
    [],
  );

  const sortButtonItems = useMemo(() => getSortOptions(sortOptions || []), [sortOptions]);

  const activeItem = sortButtonItems.find(item => sort && item.value === sort) ?? sortButtonItems[0];

  const handleItemClick = useCallback<HandleItemClick>(
    item => {
      closeDropdown();

      if (item.value !== activeItem.value) {
        boundActions.setActivePinSortWithPageReset(item.value);

        refetch();
      }
    },
    [activeItem.value, boundActions, closeDropdown, refetch],
  );

  return (
    <SortListingButton
      activeItem={activeItem}
      dataTestId="sorting-by-pin-button"
      dropdownVisibilityState={dropdownVisibilityState}
      items={sortButtonItems}
      ref={elementRef}
      onItemClick={handleItemClick}
    />
  );
}, 'SortListingByPinButtonContainer');
